import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Play, Plus, Check, ThumbsUp, VolumeX, Loader2, ExternalLink } from 'lucide-react';
import { fetchVideos, fetchDetails, getMediaType, IMG_BASE } from '../utils/tmdb';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// External free-with-ads services we deep-link to for modern licensed content
// we can't legally embed ourselves. Each opens a title-search in a new tab.
const FREE_STREAMING_SERVICES = [
  { key: 'tubi', label: 'Tubi', color: 'from-orange-500 to-yellow-500', url: (q) => `https://tubitv.com/search/${encodeURIComponent(q)}` },
  { key: 'pluto', label: 'Pluto TV', color: 'from-yellow-400 to-amber-500', url: (q) => `https://pluto.tv/en/search/details?query=${encodeURIComponent(q)}` },
  { key: 'youtube', label: 'YouTube Movies', color: 'from-red-600 to-rose-600', url: (q) => `https://www.youtube.com/results?search_query=${encodeURIComponent(q + ' full movie')}&sp=EgIYAQ%253D%253D` },
  { key: 'freevee', label: 'Freevee', color: 'from-sky-500 to-blue-600', url: (q) => `https://www.amazon.com/gp/video/search?phrase=${encodeURIComponent(q)}&ie=UTF8&filter_free=1` },
];

const MovieModal = ({ item, onClose, onListChanged, onContinueChanged }) => {
  const [videoKey, setVideoKey] = useState(null);
  const [details, setDetails] = useState(null);
  const [inList, setInList] = useState(false);
  const [busy, setBusy] = useState(false);

  const hasEmbeddedFull = !!(item?.stream_url || item?.youtube_id);

  useEffect(() => {
    if (!item) return;
    setVideoKey(null);
    setDetails(null);
    // Skip TMDB lookups for items that carry their own full-video source.
    if (item.stream_url || item.youtube_id) return;
    (async () => {
      const [k, d] = await Promise.all([fetchVideos(item), fetchDetails(item)]);
      setVideoKey(k);
      setDetails(d);
    })();
  }, [item]);

  useEffect(() => {
    if (item) document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'auto'; };
  }, [item]);

  useEffect(() => {
    if (!item) return;
    const mediaType = getMediaType(item);
    const title = item.title || item.name || item.original_name;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/mylist`);
        setInList((data.items || []).some((x) => x.item_id === String(item.id) && x.media_type === mediaType));
      } catch (e) {
        console.error('[MovieModal] Failed to check My List membership:', e);
        setInList(false);
      }
      try {
        await axios.post(`${API}/continue-watching`, {
          item_id: String(item.id),
          media_type: mediaType,
          title,
          backdrop_path: item.backdrop_path,
          poster_path: item.poster_path,
          overview: item.overview,
          progress: 0.1,
        });
        if (onContinueChanged) onContinueChanged();
      } catch (e) {
        console.error('[MovieModal] Failed to upsert continue watching:', e);
      }
    })();
  }, [item, onContinueChanged]);

  if (!item) return null;

  const title = item.title || item.name || item.original_name;
  const mediaType = getMediaType(item);
  const genres = details?.genres?.map((g) => g.name).join(' • ') || '';
  let runtime = '';
  if (details?.runtime) runtime = `${details.runtime} min`;
  else if (details?.number_of_seasons) runtime = `${details.number_of_seasons} Season${details.number_of_seasons > 1 ? 's' : ''}`;
  const year = (item.release_date || item.first_air_date || '').slice(0, 4);

  const toggleList = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (inList) {
        await axios.delete(`${API}/mylist/${mediaType}/${item.id}`);
        setInList(false);
      } else {
        await axios.post(`${API}/mylist`, {
          item_id: String(item.id),
          media_type: mediaType,
          title,
          poster_path: item.poster_path,
          backdrop_path: item.backdrop_path,
        });
        setInList(true);
      }
      if (onListChanged) onListChanged();
    } catch (e) {
      console.error('[MovieModal] Toggle list failed:', e);
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/80 flex items-start justify-center overflow-y-auto py-10 px-2"
      onClick={onClose}
    >
      <div
        className="relative bg-[#181818] w-full max-w-4xl rounded-lg overflow-hidden text-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-30 bg-[#181818] rounded-full p-2 hover:bg-black transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="relative w-full aspect-video bg-black">
          {item.stream_url ? (
            <video
              className="w-full h-full bg-black"
              src={item.stream_url}
              controls
              autoPlay
              playsInline
              poster={typeof item.backdrop_path === 'string' && item.backdrop_path.startsWith('http') ? item.backdrop_path : undefined}
            >
              Your browser does not support the video tag.
            </video>
          ) : item.youtube_id ? (
            <iframe
              className="w-full h-full"
              src={`https://www.youtube.com/embed/${item.youtube_id}?autoplay=1&rel=0`}
              title={title}
              allow="autoplay; encrypted-media; fullscreen"
              allowFullScreen
              data-testid="modal-youtube-full-iframe"
            />
          ) : videoKey ? (
            <iframe
              className="w-full h-full"
              src={`https://www.youtube.com/embed/${videoKey}?autoplay=1&mute=1&controls=1&rel=0`}
              title={title}
              allow="autoplay; encrypted-media"
              allowFullScreen
            />
          ) : (
            <img
              src={typeof item.backdrop_path === 'string' && item.backdrop_path.startsWith('http')
                ? item.backdrop_path
                : `${IMG_BASE}${item.backdrop_path || item.poster_path}`}
              alt={title}
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#181818] to-transparent pointer-events-none" />
          <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
            <h2 className="text-2xl md:text-4xl font-extrabold drop-shadow-lg">{title}</h2>
          </div>
        </div>

        <div className="p-6 md:p-8">
          <div className="flex items-center gap-3 mb-5">
            <button className="flex items-center gap-2 bg-white text-black font-semibold px-5 py-2 rounded hover:bg-white/80 transition-colors">
              <Play className="w-5 h-5 fill-black" /> Play
            </button>
            <button
              onClick={toggleList}
              disabled={busy}
              title={inList ? 'Remove from My List' : 'Add to My List'}
              className={`p-2 rounded-full border transition-colors ${
                inList ? 'bg-white text-black border-white' : 'border-white/50 hover:border-white'
              }`}
              aria-label="toggle my list"
              data-testid="modal-mylist-toggle"
            >
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : (inList ? <Check className="w-5 h-5" /> : <Plus className="w-5 h-5" />)}
            </button>
            <button className="p-2 rounded-full border border-white/50 hover:border-white transition-colors" aria-label="like">
              <ThumbsUp className="w-5 h-5" />
            </button>
            <button className="ml-auto p-2 rounded-full border border-white/50 hover:border-white transition-colors" aria-label="mute">
              <VolumeX className="w-5 h-5" />
            </button>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 text-sm mb-3">
                <span className="text-green-500 font-semibold">
                  {Math.round((item.vote_average || 0) * 10)}% Match
                </span>
                {year && <span>{year}</span>}
                {runtime && <span className="border border-white/30 px-1.5 text-xs">{runtime}</span>}
                <span className="border border-white/30 px-1.5 text-xs">HD</span>
              </div>
              <p className="text-sm md:text-base text-white/90 leading-relaxed">
                {item.overview || 'No description available.'}
              </p>

              {!hasEmbeddedFull && (
                <div className="mt-6 pt-5 border-t border-white/10">
                  <p className="text-xs uppercase tracking-wider text-white/50 mb-3">
                    Watch the full movie for free on
                  </p>
                  <div className="flex flex-wrap gap-2" data-testid="modal-external-services">
                    {FREE_STREAMING_SERVICES.map((svc) => (
                      <a
                        key={svc.key}
                        href={svc.url(title)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`modal-watch-on-${svc.key}`}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-white bg-gradient-to-r ${svc.color} hover:opacity-90 transition-opacity`}
                      >
                        {svc.label}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    ))}
                  </div>
                  <p className="mt-2 text-[10px] text-white/40">
                    These free-with-ads services host the full film. NovaReelTV links you out — we don't own or store the content.
                  </p>
                </div>
              )}
            </div>
            <div className="text-sm space-y-2">
              {genres && (<div><span className="text-white/60">Genres: </span><span>{genres}</span></div>)}
              {details?.original_language && (<div><span className="text-white/60">Language: </span><span className="uppercase">{details.original_language}</span></div>)}
              {details?.vote_count !== undefined && (<div><span className="text-white/60">Votes: </span><span>{details.vote_count?.toLocaleString()}</span></div>)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MovieModal;
